<template>
  <aside
    class="app-sidebar"
    :class="[
      { 'app-sidebar--minimized': isMinimized },
      { 'app-sidebar--open': !isMinimized },
    ]"
  >
    <!-- Brand Logo -->
    <LayoutBrandLogo :isMinimized="isMinimized" />

    <hr
      class="border-uilight-100"
      :class="[{ block: !isMinimized }, { hidden: isMinimized }]"
    />

    <div class="app-sidebar__wrapper">
      <div class="flex flex-col justify-center">
        <!-- back to main menu button when settings drawer is open -->
        <BaseButton
          no-padding
          v-if="showSettings"
          @click="showSettings = false"
          :type="'text'"
          :symbol="'chevron_left'"
          :label="$t('Back to main menu')"
          class="mx-4 my-3 app-sidebar__nav--text"
          :style="[{ 'justify-content': 'start' }]"
        />
        <BaseButton
          no-padding
          v-if="!showSettings && route.path.includes('user-settings')"
          @click="showSettings = true"
          :type="'text'"
          :symbol="'chevron_left'"
          :label="$t('Settings')"
          class="m-4 app-sidebar__nav--text"
          :style="[{ 'justify-content': 'start' }]"
        />
        <LayoutSettingsNew :isMinimized="isMinimized" v-if="showSettings" />
        <nav class="app-sidebar__nav" v-else>
          <ul class="flex flex-col gap-2">
            <li
              class="app-sidebar__drawer__menu__item"
              :class="{
                'app-animation--loading app-animation--loading--primary':
                  loadingIndex === index,
              }"
              v-for="(navbarItem, index) in navbarItems"
              :key="index"
            >
              <NuxtLink
                v-if="!navbarItem.children"
                :id="navbarItem.id"
                @click="handleClick(index, navbarItem, $event)"
                :to="localePath(navbarItem.path)"
                class="app-sidebar__nav__link--main relative"
                :class="{ active: isActive(navbarItem.path) }"
              >
                <!-- nav icon-->
                <span
                  class="material-symbols-outlined app-sidebar__nav--icon"
                  >{{ navbarItem.icon }}</span
                >
                <!-- nav text -->
                <span class="app-sidebar__nav--text">
                  {{ navbarItem.name }}</span
                >
                <!-- Tooltip for minimized sidebar -->
                <div v-if="isMinimized" class="app-sidebar__tooltip">
                  <span class="app-sidebar__tooltip__badge">
                    {{ navbarItem.name }}
                  </span>
                </div>
              </NuxtLink>
              <!-- Dropdown menu -->
              <NuxtLink
                v-else
                :id="navbarItem.id"
                @click="handleClick(index, navbarItem, $event)"
                :to="localePath(navbarItem.path)"
                class="app-sidebar__nav__link--main"
                :class="{ active: isActive(navbarItem.path) }"
              >
                <!-- nav icon-->
                <span
                  class="material-symbols-outlined app-sidebar__nav--icon"
                  >{{ navbarItem.icon }}</span
                >
                <!-- nav text -->
                <span class="app-sidebar__nav--text">
                  {{ navbarItem.name }}</span
                >
              </NuxtLink>
              <ul
                v-if="navbarItem.children"
                class="app-sidebar__submenu--transition app-sidebar__nav--text"
                :class="{
                  'app-sidebar__submenu--active': isActive(navbarItem.path),
                  'app-sidebar__submenu--inactive': !isActive(navbarItem.path),
                }"
              >
                <li v-for="subItem in navbarItem.children" :key="subItem.name">
                  <!-- 44px -->
                  <NuxtLink
                    :id="subItem.id"
                    @click="
                      handleDropdownClick({
                        path: subItem.path,
                        view: subItem.query?.view,
                      })
                    "
                    class="app-sidebar__nav__link--main"
                    :class="{
                      active: isActiveSub(subItem.path, subItem.query?.view),
                    }"
                  >
                    <!-- nav icon-->
                    <span
                      v-if="subItem.icon"
                      class="material-symbols-outlined app-sidebar__nav--icon"
                      >{{ subItem.icon }}</span
                    >
                    <!-- nav text -->
                    <span class="app-sidebar__nav--text">
                      {{ subItem.name }}</span
                    >
                  </NuxtLink>
                </li>
              </ul>
              <!-- Tooltip for minimized sidebar -->
              <div v-if="isMinimized" class="app-sidebar__tooltip">
                <div
                  class="app-sidebar__tooltip__badge"
                  :class="{
                    'app-sidebar__tooltip__badge--children':
                      navbarItem.children,
                  }"
                >
                  <span v-if="!navbarItem.children">{{ navbarItem.name }}</span>
                  <div v-if="navbarItem.children">
                    <div class="app-sidebar__tooltip__children">
                      <div
                        v-for="child in navbarItem.children"
                        :key="child.name"
                        class="app-sidebar__tooltip__child"
                        :class="{
                          'app-sidebar__tooltip__child--active': isActiveSub(
                            child.path,
                            child.query?.view
                          ),
                        }"
                        @click="
                          handleDropdownClick({
                            path: child.path,
                            view: child.query?.view,
                          })
                        "
                      >
                        {{ child.name }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </nav>
        <!-- Minimize Button -->
        <div class="">
          <div
            class="app-sidebar__minimize-btn hidden app-sidebar__expand-button"
            :class="{
              'justify-end': !isMinimized,
              'justify-center': isMinimized,
            }"
          >
            <button
              @click="toggleMinimized"
              class="flex items-center justify-center h-8 w-8 rounded-full"
            >
              <span
                class="material-symbols-outlined text-text-secondary hover:text-primary"
              >
                {{ isMinimized ? 'chevron_right' : 'chevron_left' }}
              </span>
            </button>
            <div class="app-sidebar__tooltip__expand-button">
              <span class="app-sidebar__tooltip__badge">
                {{ $t('Expand') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <BaseButton
          v-if="!isMinimized"
          small
          style="justify-content: start"
          class="ml-3"
          :type="'text--gray'"
          :label="$t('Help')"
          :symbol="'help'"
          @click="helpModal"
        />
        <div v-else class="app-sidebar__help-button">
          <BaseButton
            small
            class=""
            :type="'text--gray'"
            :symbol="'help'"
            @click="helpModal"
          />
          <!-- Tooltip for help button when minimized -->
          <div class="app-sidebar__tooltip__help-button">
            <span class="app-sidebar__tooltip__badge">
              {{ $t('Help') }}
            </span>
          </div>
        </div>
        <!-- Render a NavbarSearch unless we're on restricted pages -->
        <NavbarSearchNew
          v-if="!pagesWithoutSearch.includes(currentPage)"
          name="term"
          class="app-sidebar__nav"
        />

        <hr class="border-uilight-100" />

        <!-- User: loggedin -->
        <nav v-if="userProfile" class="app-sidebar__nav">
          <ul class="flex flex-col gap-4">
            <li
              v-if="$features?.notifications"
              class="app-sidebar__drawer__menu__item"
              :class="{
                'app-animation--loading app-animation--loading--primary':
                  loadingIndex === 5,
              }"
              :key="5"
            >
              <div
                class="dropdown dropdown-right dropdown-end app-sidebar__nav__link--secondary"
                ref="bellContainerRef"
                :class="{ active: isActive('/notifications') }"
              >
                <div
                  role="button"
                  tabindex="0"
                  class="flex items-center gap-4"
                  @click="bellIconClicked()"
                  @dblclick="bellDblClick"
                >
                  <div class="app-sidebar__nav--secondary">
                    <span
                      class="material-symbols-outlined app-sidebar__nav--icon"
                      >notifications</span
                    >
                    <div
                      v-if="notificationStore.count > 0"
                      class="absolute top-0 right-0 bg-danger text-white text-xs rounded-full h-2 w-2 flex items-center justify-center"
                    ></div>
                    <!-- Tooltip for minimized sidebar -->
                    <div
                      v-if="isMinimized && !notificationsDropdownOpen"
                      class="app-sidebar__tooltip"
                    >
                      <span class="app-sidebar__tooltip__badge">
                        {{ $t('Notifications') }}
                      </span>
                    </div>
                  </div>
                  <!-- nav text -->
                  <span class="app-sidebar__nav--text">{{
                    $t('Notifications')
                  }}</span>
                </div>
                <LayoutNotifications
                  v-if="notificationsDropdownOpen"
                  :isMinimized="isMinimized"
                  @close="notificationsDropdownOpen = false"
                />
              </div>
            </li>
            <li
              class="app-sidebar__drawer__menu__item"
              :class="{
                'app-animation--loading app-animation--loading--primary':
                  loadingIndex === 6,
              }"
              :key="6"
            >
              <div
                class="dropdown dropdown-right dropdown-end app-sidebar__nav__link--secondary relative"
                ref="activityContainerRef"
                :id="'onboarding-desk2'"
                :class="{ active: isActive('/activity/all') }"
              >
                <div
                  role="button"
                  class="flex items-center gap-4"
                  tabindex="0"
                  @click="activityDropdownOpen = !activityDropdownOpen"
                >
                  <div class="app-sidebar__nav--secondary">
                    <span
                      class="material-symbols-outlined app-sidebar__nav--icon"
                      >settings</span
                    >
                    <!-- Tooltip for minimized sidebar -->
                    <div
                      v-if="isMinimized && !activityDropdownOpen"
                      class="app-sidebar__tooltip"
                    >
                      <span class="app-sidebar__tooltip__badge">
                        {{ $t('Processes & Activity') }}
                      </span>
                    </div>
                  </div>
                  <span class="app-sidebar__nav--text">{{
                    $t('Processes & Activity')
                  }}</span>
                </div>
                <ul
                  v-if="activityDropdownOpen"
                  tabindex="0"
                  class="absolute bottom-0 app-sidebar__dropdown__menu bg-white w-max"
                  :style="{ left: isMinimized ? '60px' : '270px' }"
                >
                  <li
                    class="app-sidebar__dropdown__item app-sidebar__dropdown__item__title"
                  >
                    <NuxtLink
                      :to="localePath('/activity/recognition')"
                      class="app-sidebar__link"
                    >
                      <i class="mdi mdi-text-recognition mr-2"></i>
                      {{ $t('Recognition Runs') }}
                    </NuxtLink>
                    <NuxtLink
                      :to="localePath('/data-lab')"
                      class="app-sidebar__link"
                    >
                      <i class="mdi mdi-brain mr-2"></i>
                      {{ $t('Training Runs') }}
                    </NuxtLink>
                    <NuxtLink
                      :to="localePath('/activity/up-download')"
                      class="app-sidebar__link"
                    >
                      <i class="mdi mdi-swap-vertical mr-2"></i>
                      {{ $t('Up- & Downloads') }}
                    </NuxtLink>

                    <NuxtLink
                      :to="localePath('/activity/advanced')"
                      class="app-sidebar__link"
                    >
                      <i class="mdi mdi-cogs mr-2"></i>
                      {{ $t('Other Processes') }}
                    </NuxtLink>
                    <NuxtLink
                      v-if="hasHermesAccess"
                      :to="localePath('/activity/hermes')"
                      class="app-sidebar__link"
                    >
                      <i class="mdi mdi-lan mr-2"></i> {{ $t('Hermes') }}
                    </NuxtLink>
                    <NuxtLink
                      v-if="userProfile?.IsAdmin"
                      :to="localePath('/activity/all')"
                      class="app-sidebar__link"
                    >
                      <i class="mdi mdi-view-list mr-2"></i>
                      {{ $t('All Jobs') }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </li>

            <li
              class="app-sidebar__drawer__menu__item"
              :class="{
                'app-animation--loading app-animation--loading--primary':
                  loadingIndex === 7,
              }"
              :key="7"
            >
              <div
                class="dropdown dropdown-right dropdown-end app-sidebar__nav__link--secondary relative"
                ref="settingsContainerRef"
                :id="'onboarding-desk3'"
                :class="{ active: isActive('/user-settings') }"
              >
                <div
                  role="button"
                  class="flex items-center gap-4"
                  tabindex="0"
                  @click="settingsDropdownOpen = !settingsDropdownOpen"
                >
                  <div class="app-sidebar__nav--secondary">
                    <span
                      class="material-symbols-outlined app-sidebar__nav--icon"
                      >person</span
                    >
                    <!-- Tooltip for minimized sidebar -->
                    <div
                      v-if="isMinimized && !settingsDropdownOpen"
                      class="app-sidebar__tooltip"
                    >
                      <span class="app-sidebar__tooltip__badge">
                        {{ $t('Settings & Account') }}
                      </span>
                    </div>
                  </div>
                  <span class="app-sidebar__nav--text">{{
                    $t('Settings & Account')
                  }}</span>
                </div>
                <ul
                  v-if="settingsDropdownOpen"
                  tabindex="0"
                  class="app-sidebar__dropdown__menu absolute bottom-0 bg-white w-fit"
                  :style="{ left: isMinimized ? '60px' : '270px' }"
                >
                  <li
                    class="app-sidebar__dropdown__item app-sidebar__dropdown__item__title"
                  >
                    <span class="app-sidebar__dropdown__item__title__text">
                      {{ $t('Signed in as') }}
                      <strong> {{ userProfile.Email }}</strong></span
                    >
                  </li>
                  <li
                    class="app-sidebar__dropdown__item app-sidebar__dropdown__item--collapse"
                  >
                    <input type="checkbox" />
                    <div class="collapse-title">
                      <i class="mdi mdi-earth"></i>
                      <span>{{ $t('Language') }}</span>
                    </div>
                    <div class="collapse-content">
                      <ul>
                        <li
                          v-for="(locale, index) in availableLocales"
                          :key="index"
                          :class="[
                            'app-sidebar__item focus:bg-transparent',
                            { 'bg-[#e8ebf0]': locale.code === currentLocale },
                          ]"
                        >
                          <span @click="setAndStoreLocale(locale.code)">
                            <img
                              class="h-5 w-5"
                              loading="lazy"
                              :src="`/images/flags/${locale.label.toLowerCase()}.svg`"
                            />
                            <span class="is-uppercase">{{ locale.label }}</span>
                          </span>
                        </li>
                      </ul>
                    </div>
                  </li>
                  <hr class="w-full border-gray-50 my-1" />
                  <li class="app-sidebar__dropdown__item">
                    <NuxtLink
                      :to="
                        localePath({
                          name: 'user-settings',
                          query: {
                            view: 'personal',
                          },
                        })
                      "
                    >
                      <i class="mdi mdi-cog"></i>
                      <span>{{ $t('Account & Usage') }}</span>
                    </NuxtLink>
                  </li>
                  <li class="app-sidebar__dropdown__item">
                    <NuxtLink to="/invitations">
                      <i class="mdi mdi-email"></i>
                      <span>{{ $t('Invitations') }}</span>
                    </NuxtLink>
                  </li>
                  <hr class="w-full border-gray-50 my-1" />
                  <li class="app-sidebar__dropdown__item">
                    <NuxtLink
                      :to="
                        localePath({
                          name: 'changelog',
                        })
                      "
                    >
                      <i class="mdi mdi-creation"></i>
                      <span>{{ $t('Whats new') }}</span>
                    </NuxtLink>
                  </li>
                  <li class="app-sidebar__dropdown__item">
                    <a @click="showPrivacySettings">
                      <i class="mdi mdi-cookie"></i>
                      <span>{{ $t('Privacy Settings') }}</span>
                    </a>
                  </li>
                  <li class="app-sidebar__dropdown__item">
                    <NuxtLink
                      to="https://legal.transkribus.org/"
                      target="_blank"
                    >
                      <i class="mdi mdi-scale-balance"></i>
                      <span>{{ $t('Legal Information') }}</span>
                    </NuxtLink>
                  </li>
                  <li
                    v-if="userProfile?.IsAdmin"
                    class="app-sidebar__dropdown__item"
                  >
                    <a @click="togglePosthog()">
                      <i
                        :class="
                          posthogEnabled
                            ? 'mdi mdi-eye-outline'
                            : 'mdi mdi-eye-off-outline'
                        "
                        class="app-sidebar__dropdown__icon"
                        aria-hidden="true"
                      ></i>
                      <span v-if="posthogEnabled">{{
                        $t('Disable posthog')
                      }}</span>
                      <span v-else>{{ $t('Enable posthog') }}</span>
                    </a>
                  </li>
                  <hr class="w-full border-gray-50 my-1" />
                  <li class="app-sidebar__dropdown__item">
                    <a @click="logout">
                      <i class="mdi mdi-logout"></i>
                      <span>{{ $t('Logout') }}</span>
                    </a>
                  </li>
                  <!-- <li>
                    <div class="empty-hidden">
                      <DashboardCallToAction location="user-menu" />
                    </div>
                  </li> -->
                </ul>
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </aside>
</template>

<script lang="ts" setup>
import HelpModal from '~/components/Modals/Help.vue'
const { $bus, $posthog, $features, $sentry }: any = useNuxtApp()
const { locale: currentLocale, setLocale, t } = useI18n()
const i18n = useI18n()
const availableLocales = i18n.locales
const localePath = useLocalePath()
const route = useRoute()
const configStore = useConfig()
const userApi = useUser()
const { userProfile, keycloak } = useKeycloak()
const router = useRouter()
const notificationsApi = useNotifications()

const { hermesService } = useHermesService()
const { hasAccess: hasHermesAccess } = useHermesAccess()

const loadingIndex = ref<number | null>(null)
const bellContainerRef = ref<HTMLElement | null>(null)
const activityContainerRef = ref<HTMLElement | null>(null)
const settingsContainerRef = ref<HTMLElement | null>(null)
const notificationStore = useNotificationsStore()
const showSettings = ref(false)
const posthogEnabled = ref(!$posthog.has_opted_out_capturing()) // Initialize with current state
const notificationsDropdownOpen = ref<boolean>(false)
const settingsDropdownOpen = ref<boolean>(false)
const activityDropdownOpen = ref<boolean>(false)

/**
 * PAGES that should NOT show the NavbarSearch.
 */
const pagesWithoutSearch = [
  '/sites',
  '/models',
  '/data-lab',
  '/training',
  '/training/gallery',
  '/datasets',
]

onMounted(async () => {
  document.documentElement.style.setProperty(
    '--enable-overflow-hidden',
    !$features.newSidebar ? 'true' : 'false'
  )

  // if the route includes user-settings, switch the drawer
  if (route.path.includes('user-settings')) {
    showSettings.value = true
  } else {
    showSettings.value = false
  }
})

// watcher for route changes
watch(
  () => route.path,
  () => {
    if (route.path.includes('user-settings')) {
      showSettings.value = true
    } else {
      showSettings.value = false
    }
  }
)

const currentPage = computed(() => {
  if (route.path.includes('collection') && $features.deskMenuSplit)
    return '/collection/overview'
  if (route.path.includes('collection') && !$features.deskMenuSplit)
    return '/collection'
  if (route.path.includes('training')) return '/training'
  if (route.path.includes('models')) return '/models'
  if (route.path.includes('data-lab')) return '/models'
  if (route.path.includes('datasets')) return '/datasets'
  if (route.path.includes('sites')) return '/sites'
  if (route.path.includes('learn')) return '/learn'
  if (route.path.includes('quality-control')) return '/quality-control'
  if (
    route.path.includes('user-settings') ||
    route.path.includes('invitations') ||
    route.path.includes('changelog')
  )
    return '/user-settings'
  if (route.path.includes('activity')) return '/activity/all'
  if (route.path.includes('notifications')) return '/notifications'
  else return '/'
})

const navbarItems = computed(() => {
  const baseItems = [
    {
      name: 'Home',
      path: '/',
      icon: 'account_balance',
      id: 'onboarding-desk4',
    },
    {
      name: 'Desk',
      // path: '/collection/overview',
      path: '/collection',
      icon: 'space_dashboard',
      id: 'onboarding-desk1',
      // children: [
      //   {
      //     name: 'Overview',
      //     path: '/collection/overview',
      //   },
      //   {
      //     name: 'Collections',
      //     path: '/collection',
      //   },
      // ],
    },
    {
      name: 'Models',
      path: '/models',
      icon: 'model_training',
      id: '',
      children: [
        {
          name: 'Models',
          path: '/models',
        },
        {
          name: 'Training lab',
          path: '/data-lab',
        },
      ],
    },
    {
      name: 'Sites',
      path: '/sites',
      icon: 'web_asset',
      id: '',
      children: [
        {
          name: 'My sites',
          path: '/sites',
          query: { view: 'private' },
        },
        {
          name: 'Public sites',
          path: '/sites',
          query: { view: 'public' },
        },
      ],
    },
  ]
  if ($features.deskMenuSplit) {
    baseItems[1].path = '/collection/overview'
    baseItems[1].children = [
      {
        name: 'Overview',
        path: '/collection/overview',
      },
      {
        name: 'Collections',
        path: '/collection',
      },
    ]
  }
  if (userProfile.IsAdmin) {
    baseItems[3].children.push({
      name: 'Admin',
      path: '/sites',
      query: { view: 'admin' },
    })
  }
  if ($features.dataSets) {
    baseItems.push({ name: 'Datasets', path: '/datasets', icon: 'forum' })
  }
  return baseItems
})

const helpModal = () => {
  const modal = {
    modalContent: {
      component: HelpModal,
    },
  }
  $bus.emit('open-modal', { modal: modal, props: { isMedium: true } })
}

watch(
  () => route.fullPath,
  () => {
    setTimeout(() => {
      loadingIndex.value = null
    }, 100)
  }
)

watch(posthogEnabled, newValue => {
  if (newValue) {
    $posthog.opt_in_capturing()
  } else {
    $posthog.opt_out_capturing()
  }
})

const bellDblClick = async () => {
  await notificationsApi.testNotification()
}

const handleClickOutside = (event: MouseEvent) => {
  // Check if the click target is outside both the bell icon and dropdown
  const target = event.target as Node

  // Check if click is inside bell container
  const isClickInsideBell = bellContainerRef.value?.contains(target) || false
  const isClickInsideActivity =
    activityContainerRef.value?.contains(target) || false
  const isClickInsideSettings =
    settingsContainerRef.value?.contains(target) || false

  // Only close if clicking outside both elements
  if (!isClickInsideBell) {
    notificationsDropdownOpen.value = false
  }
  if (!isClickInsideActivity) {
    activityDropdownOpen.value = false
  }
  if (!isClickInsideSettings) {
    settingsDropdownOpen.value = false
  }
}

const bellIconClicked = async () => {
  if (notificationsDropdownOpen.value) {
    notificationsDropdownOpen.value = false
    return
  }
  notificationsDropdownOpen.value = true
}

function logout() {
  // Clear hermes cookies to prevent token mismatch when switching users
  hermesService.clearTokens()

  // Proceed with normal logout
  keycloak?.logout()
  $posthog.reset()
}

function togglePosthog() {
  posthogEnabled.value = !posthogEnabled.value // Toggle the state
}

const showPrivacySettings = () => {
  if ((window as any).UC_UI) {
    ;(window as any).UC_UI.showSecondLayer()
  }
}

function setAndStoreLocale(newLocale: string) {
  if (configStore.config == null) return
  configStore.config.locale = newLocale
  setLocale(newLocale)
  userApi.updateUserConfig({
    userId: userProfile?.UserId,
    config: { locale: newLocale },
  })
}

function toggleSettings() {
  showSettings.value = true
  handleClick(
    7,
    {
      name: 'Settings & Account',
      path: '/user-settings',
      icon: 'person',
    },
    $event
  )
}

function handleClick(index: number, navbarItem: any, event: any): void {
  // do not set loading index if the item is already active
  // if the item is active, the page will not reload,
  // thus we should not show the loading animation
  if (
    navbarItem &&
    !isActive(navbarItem.path) &&
    !(event.ctrlKey || event.metaKey)
  ) {
    loadingIndex.value = index
  }
}

function handleDropdownClick(options: { path: string; view?: string }) {
  if (options.view) {
    router.push({
      path: options.path,
      query: { view: options.view },
    })
  } else {
    router.push({
      path: options.path,
    })
  }
}

function isActive(path: string): boolean {
  return path === currentPage.value
}

function isActiveSub(path: string, query: string): boolean {
  // Special handling for collection overview and collection pages
  if (path === '/collection/overview') {
    return route.path === '/collection/overview'
  }
  if (path === '/collection') {
    return (
      route.path.includes('/collection') &&
      route.path !== '/collection/overview'
    )
  }

  return route.path.includes(path) && route.query.view === query
}

const isMinimized = ref<boolean>(false)

function toggleMinimized() {
  isMinimized.value = !isMinimized.value
  localStorage.setItem('sidebarMinimized', isMinimized.value ? 'true' : 'false')
  $bus.emit("isMinimised")
}

function handleResize() {
  const windowWidth = window.innerWidth
  const breakpoint = 1024 // lg breakpoint

  // If window width is less than breakpoint, minimize the sidebar
  if (windowWidth < breakpoint) {
    isMinimized.value = true
  } else {
    // If window width is greater than breakpoint, restore from localStorage preference
    const savedState = localStorage.getItem('sidebarMinimized')
    isMinimized.value = savedState === 'true'
  }
}

onMounted(() => {
  const savedState = localStorage.getItem('sidebarMinimized')
  if (savedState === 'true') {
    isMinimized.value = true
  }

  if (userProfile?.Email) {
    $sentry.setUser({
      email: userProfile.Email,
    })
  }

  handleResize()
  window.addEventListener('resize', handleResize)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', handleClickOutside)
})
</script>
